import { Role } from '@prisma/client';
import { ListMembersResponse, MemberPermission, MemberPermissionType } from '@zond/api-interfaces';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@zond/ui';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUpdateMember } from '../../members/members.queries';
import { MemberRoleForm } from './components/member-role-form';
import { DialogProps, MemberFormData } from './types';

interface EditMemberDialogProps extends DialogProps {
  member: ListMembersResponse[0];
}

export const EditMemberDialog = ({
  isOpen,
  onOpenChange,
  onSuccess,
  member,
  triggerButtonProps,
}: EditMemberDialogProps) => {
  const { t } = useTranslation('settings');
  const [formData, setFormData] = useState<MemberFormData>({
    role: Role.USER,
    permissions: {
      [MemberPermission.TODO]: { type: MemberPermissionType.Simple, available: false },
    },
  });

  useEffect(() => {
    setFormData({
      role: member.roles[0],
      permissions: member.permissions || {
        [MemberPermission.TODO]: { type: MemberPermissionType.Simple, available: false },
      },
    });
  }, [member]);

  const updateMember = useUpdateMember(
    () => {
      toast.success(t('members.toasts.memberSettingsUpdated'));
      onSuccess?.();
      onOpenChange(false);
    },
    () => {
      toast.error(t('members.toasts.memberSettingsUpdateFailed'));
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMember.mutate({
      memberId: member.id,
      data: {
        role: formData.role,
        permissions: formData.permissions,
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={triggerButtonProps?.variant || 'ghost'} className={triggerButtonProps?.className}>
          {triggerButtonProps?.children || t('members.editDialog.trigger')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('members.editDialog.title')}</DialogTitle>
          <DialogDescription>{t('members.editDialog.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <div className="text-sm">
              <span className="text-muted-foreground">{t('members.editDialog.email')} </span>
              <span>{member.email}</span>
            </div>
            <MemberRoleForm value={formData} onChange={setFormData} />
          </div>
          <Button type="submit" className="w-full" disabled={updateMember.isPending}>
            {updateMember.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('members.editDialog.submit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
